import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import styles from './BookWordList.module.css';
import { AlertIcon, BookIcon, LoaderIcon, ArrowLeftIcon } from '../components/Icons';
import WordsSection from '../components/chapter/WordsSection/WordsSection';
import WordModal from '../components/chapter/WordModal/WordModal';

const BookWordList = () => {
  const { bookKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const initialGroup = location.state?.bookGroup;
  const initialMeta = location.state?.bookMeta || {};

  const [words, setWords] = useState(initialGroup?.items || []);
  const [loading, setLoading] = useState(!initialGroup);
  const [error, setError] = useState(initialGroup ? null : '无法加载收藏单词，请从历史页面重新进入。');
  const [wordShowDefinitions, setWordShowDefinitions] = useState(false);
  const [wordShowSentences, setWordShowSentences] = useState(false);
  const [wordFavoriteLoadingMap, setWordFavoriteLoadingMap] = useState({});
  const [expandedSentences, setExpandedSentences] = useState({});
  const expandedSentencesRef = useRef(expandedSentences);
  const [sentenceLoading, setSentenceLoading] = useState({});
  const [chapterMetadata, setChapterMetadata] = useState({});
  const [chapterMetadataLoading, setChapterMetadataLoading] = useState(false);
  const [chapterMetadataError, setChapterMetadataError] = useState(null);
  const [wordModalOpen, setWordModalOpen] = useState(false);
  const [wordModalActiveDict, setWordModalActiveDict] = useState('youdao');
  const [wordModalDictResults, setWordModalDictResults] = useState({ youdao: null, collins: null });
  const [wordModalLoading, setWordModalLoading] = useState({ youdao: false, collins: false });
  const [selectedWordLookup, setSelectedWordLookup] = useState(null);
  const [selectedWordItem, setSelectedWordItem] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const bookId = useMemo(() => {
    if (initialGroup?.bookId != null) {
      return String(initialGroup.bookId);
    }
    return bookKey ? decodeURIComponent(bookKey) : 'unknown-book';
  }, [initialGroup?.bookId, bookKey]);

  const bookTitle = initialGroup?.bookTitle || initialMeta.title || `书籍 ${bookId}`;
  const author = initialMeta.author || '';

  useEffect(() => {
    expandedSentencesRef.current = expandedSentences;
  }, [expandedSentences]);

  const handleBackToHistory = useCallback(() => {
    navigate('/history/words', {
      state: {
        historyView: { activeView: 'words' },
        refreshFavorites: hasChanges,
      },
    });
  }, [navigate, hasChanges]);

  const getWordKey = (word) => {
    const raw =
      word.id ?? word.favorite_id ?? word.favoriteId ?? word.word_id ?? word.wordId;
    return String(raw ?? '');
  };

  const fetchSentenceDetailForWord = useCallback(async (word) => {
    const sentenceIndex = Number(word.sentence_id) || 0;

    try {
      const response = await api.get(`/chapters/${word.chapter_id}`);
      const chapter = response.data;

      let contentSentences = [];
      let translationSentences = [];

      if (chapter.content) {
        try {
          const contentData = JSON.parse(chapter.content);
          if (Array.isArray(contentData?.sentences)) {
            contentSentences = contentData.sentences
              .map(item => item.sentence_content || item.content || '')
              .filter(text => text.trim());
          } else {
            const text = contentData.sentence_content || contentData.content || '';
            if (text) contentSentences = [text];
          }
        } catch (e) {
          contentSentences = (chapter.content || '')
            .split(/\n+/)
            .filter(p => p.trim());
        }
      }

      if (chapter.content_trans) {
        try {
          const transData = JSON.parse(chapter.content_trans);
          if (Array.isArray(transData)) {
            translationSentences = transData
              .map(item => item.sentence_content || item.content || '')
              .filter(text => text.trim());
          } else if (Array.isArray(transData.sentences)) {
            translationSentences = transData.sentences
              .map(item => item.sentence_content || item.content || '')
              .filter(text => text.trim());
          } else {
            const text = transData.sentence_content || transData.content || '';
            if (text) translationSentences = [text];
          }
        } catch (e) {
          translationSentences = (chapter.content_trans || '')
            .split(/\n+/)
            .filter(p => p.trim());
        }
      }

      return {
        originalText: contentSentences[sentenceIndex] || '',
        translatedText: translationSentences[sentenceIndex] || '',
        chapterTitle: chapter.title,
      };
    } catch (error) {
      console.error('获取句子详情失败:', error);
      return null;
    }
  }, []);

  const pendingChapterIds = useMemo(() => {
    if (!words.length) {
      return [];
    }
    const missing = new Set();
    words.forEach((word) => {
      const rawId = word.chapter_id;
      if (rawId === undefined || rawId === null) {
        return;
      }
      const key = String(rawId);
      if (!chapterMetadata[key]) {
        missing.add(key);
      }
    });
    return Array.from(missing);
  }, [words, chapterMetadata]);

  useEffect(() => {
    if (!pendingChapterIds.length) {
      setChapterMetadataLoading(false);
      return;
    }

    let cancelled = false;

    const loadChapterMetadata = async () => {
      setChapterMetadataLoading(true);
      setChapterMetadataError(null);

      const responses = await Promise.allSettled(
        pendingChapterIds.map(async (chapterId) => {
          try {
            const response = await api.get(`/chapters/${chapterId}`);
            const chapter = response.data;

            let displayTitle = chapter.title;
            try {
              const contentData = JSON.parse(chapter.content);
              if (contentData?.chapter_title) {
                displayTitle = contentData.chapter_title;
              }
            } catch (e) {
              // ignore
            }

            const volumeInfo = chapter.volume || {};
            const novelInfo = chapter.novel || volumeInfo.novel || {};

            return {
              chapterId,
              data: {
                id: chapter.id,
                title: displayTitle || chapter.title,
                orderIndex: chapter.order_index,
                rawTitle: chapter.title,
                volumeId: chapter.volume_id ?? volumeInfo.id,
                volumeTitle: chapter.volume_title || volumeInfo.title,
                volumeOrder: chapter.volume_order_index ?? volumeInfo.order_index,
                novelId: chapter.novel_id ?? novelInfo.id,
                novelTitle: chapter.novel_title || novelInfo.title || '未命名书籍',
              },
            };
          } catch (error) {
            console.error('加载章节信息失败:', error);
            throw error;
          }
        })
      );

      if (cancelled) {
        return;
      }

      const newEntries = {};
      let hasError = false;

      responses.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { chapterId, data } = result.value;
          newEntries[String(chapterId)] = data;
        } else {
          hasError = true;
        }
      });

      if (Object.keys(newEntries).length) {
        setChapterMetadata((prev) => ({ ...prev, ...newEntries }));
      }
      if (hasError) {
        setChapterMetadataError('部分章节信息加载失败');
      }
      setChapterMetadataLoading(false);
    };

    loadChapterMetadata();
    return () => {
      cancelled = true;
    };
  }, [pendingChapterIds]);

  useEffect(() => {
    if (!wordShowSentences) {
      if (Object.keys(expandedSentencesRef.current).length) {
        setExpandedSentences({});
      }
      setSentenceLoading({});
      return undefined;
    }

    let cancelled = false;

    const loadDetails = async () => {
      for (const word of words) {
        if (cancelled) break;
        const key = getWordKey(word);
        if (!key) continue;
        if (expandedSentencesRef.current[key]) {
          setSentenceLoading(prev => {
            if (!prev[key]) return prev;
            const { [key]: _removed, ...rest } = prev;
            return rest;
          });
          continue;
        }

        setSentenceLoading(prev => ({ ...prev, [key]: true }));
        const detail = await fetchSentenceDetailForWord(word);

        if (cancelled) {
          setSentenceLoading(prev => {
            if (!prev[key]) return prev;
            const { [key]: _removed, ...rest } = prev;
            return rest;
          });
          return;
        }

        if (detail) {
          setExpandedSentences(prev => {
            if (prev[key]) return prev;
            return { ...prev, [key]: detail };
          });
        }

        setSentenceLoading(prev => {
          if (!prev[key]) return prev;
          const { [key]: _removed, ...rest } = prev;
          return rest;
        });
      }
    };

    loadDetails();
    return () => {
      cancelled = true;
    };
  }, [wordShowSentences, words, fetchSentenceDetailForWord]);

  useEffect(() => {
    if (!initialGroup) {
      setLoading(false);
    }
  }, [initialGroup]);

  const handleDeleteWord = async (wordItem) => {
    const wordId = wordItem.id;
    if (wordId == null) return;

    setWordFavoriteLoadingMap(prev => ({ ...prev, [wordId]: true }));
    try {
      await api.delete(`/favorite-words/${wordId}`);
      setWords(prev => prev.filter(word => word.id !== wordId));
      setExpandedSentences(prev => {
        if (!prev) return prev;
        const key = getWordKey(wordItem);
        if (key && prev[key]) {
          const { [key]: _removed, ...rest } = prev;
          return rest;
        }
        return prev;
      });
      setHasChanges(true);
    } catch (err) {
      console.error('删除收藏单词失败:', err);
      alert('删除失败，请稍后重试');
    } finally {
      setWordFavoriteLoadingMap(prev => {
        const { [wordId]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleOpenWordModal = (wordItem) => {
    if (!wordItem?.word) {
      return;
    }

    const rawWord = typeof wordItem.word === 'string' ? wordItem.word.trim() : '';
    if (!rawWord) {
      return;
    }

    const wordData = wordItem.word_jsondata || {};
    const youdaoDefinition =
      wordData.youdao_definition ||
      wordData.youdaoDefinition ||
      wordData.definition ||
      null;
    const collinsDefinition = wordData.collins_definition || wordData.collinsDefinition || null;

    setSelectedWordLookup({
      word: rawWord,
      success: true,
      youdao_definition: youdaoDefinition,
      collins_definition: collinsDefinition,
      phonetic: wordData.phonetic || wordData.phonetic_us || wordData.phonetic_uk || '',
    });
    setSelectedWordItem(wordItem);
    setWordModalDictResults({
      youdao: youdaoDefinition
        ? { success: true, data: { definition: youdaoDefinition } }
        : null,
      collins: collinsDefinition
        ? { success: true, data: { definition: collinsDefinition } }
        : null,
    });
    const defaultDict = youdaoDefinition ? 'youdao' : collinsDefinition ? 'collins' : 'youdao';
    setWordModalActiveDict(defaultDict);
    setWordModalLoading({ youdao: false, collins: false });
    setWordModalOpen(true);
  };

  const handleCloseWordModal = () => {
    setWordModalOpen(false);
    setSelectedWordLookup(null);
    setSelectedWordItem(null);
    setWordModalDictResults({ youdao: null, collins: null });
    setWordModalActiveDict('youdao');
    setWordModalLoading({ youdao: false, collins: false });
  };

  const handleWordModalDictTabClick = (dictKey) => {
    if (!dictKey) return;
    if (dictKey === 'mnemonic') {
      setWordModalActiveDict('mnemonic');
      return;
    }
    if (dictKey === 'youdao' || dictKey === 'collins') {
      setWordModalActiveDict(dictKey);
    }
  };

  const handleWordModalFavoriteToggle = async () => {
    if (!selectedWordItem) {
      return;
    }
    await handleDeleteWord(selectedWordItem);
    if (!words.length) {
      handleCloseWordModal();
    }
  };

  const handleWordModalPronounce = (word, voiceType = 0) => {
    const cleanWord = typeof word === 'string' ? word.trim() : '';
    if (!cleanWord || typeof Audio === 'undefined') {
      return;
    }
    try {
      const audio = new Audio(`https://dict.youdao.com/dictvoice?type=${voiceType}&audio=${encodeURIComponent(cleanWord)}`);
      audio.play().catch((error) => {
        console.error('播放单词发音失败:', error);
      });
    } catch (error) {
      console.error('播放单词发音失败:', error);
    }
  };

  const handleGoToChapterWithHighlight = async (chapterId, sentenceId) => {
    const chapterKey = String(chapterId);
    const cachedMeta = chapterMetadata[chapterKey];

    if (cachedMeta?.novelId) {
      navigate(`/read/${cachedMeta.novelId}/${chapterId}?highlight=${sentenceId}`);
      return;
    }

    try {
      const response = await api.get(`/chapters/${chapterId}`);
      const chapter = response.data;
      const bookIdForChapter = chapter.novel_id || chapter.novel?.id || chapter.volume?.novel?.id;

      if (!bookIdForChapter) {
        throw new Error('缺少小说信息');
      }

      navigate(`/read/${bookIdForChapter}/${chapterId}?highlight=${sentenceId}`);
    } catch (error) {
      console.error('获取章节信息失败:', error);
      alert('无法跳转到原文，请稍后重试');
    }
  };

  const handleStartWordStudy = () => {
    navigate(`/learn/${encodeURIComponent(bookId)}`, {
      state: {
        from: 'historyWords',
        backTo: {
          pathname: location.pathname,
          search: location.search,
          state: location.state,
        },
        historyReturn: {
          activeView: 'words',
        },
      },
    });
  };

  const uniqueChapterCount = useMemo(() => {
    const unique = new Set();
    words.forEach((word) => {
      if (word.chapter_id !== undefined && word.chapter_id !== null) {
        unique.add(String(word.chapter_id));
      }
    });
    return unique.size;
  }, [words]);

  const sampleWords = useMemo(() => {
    const seen = new Set();
    const samples = [];
    for (const item of words) {
      if (samples.length >= 3) break;
      const text = typeof item.word === 'string' ? item.word.trim() : '';
      if (!text || seen.has(text)) continue;
      seen.add(text);
      samples.push(text);
    }
    return samples;
  }, [words]);

  const wordModalFavoriteLoading = selectedWordItem
    ? !!wordFavoriteLoadingMap[selectedWordItem.id]
    : false;
  const wordModalFavorited = Boolean(
    selectedWordItem && words.some(word => word.id === selectedWordItem.id)
  );
  const mergedWordModalLoading = {
    ...wordModalLoading,
    mnemonic: wordModalLoading.mnemonic ?? false,
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.stateContainer}>
          <LoaderIcon size={28} />
          <span>加载收藏单词中...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.stateContainer}>
          <AlertIcon size={36} />
          <span>{error}</span>
          <button type="button" className={styles.backLink} onClick={handleBackToHistory}>
            返回收藏
          </button>
        </div>
      );
    }

    if (!words.length) {
      return (
        <div className={styles.stateContainer}>
          <BookIcon size={36} />
          <span>该书暂时没有收藏单词</span>
          <button type="button" className={styles.backLink} onClick={handleBackToHistory}>
            返回收藏
          </button>
        </div>
      );
    }

    return (
      <div className={styles.wordList}>
        <div className={styles.listHeader}>
          <div className={styles.bookInfo}>
            <h1 className={styles.bookTitle}>{bookTitle}</h1>
            <div className={styles.metaRow}>
              <span className={styles.metaItem}>单词 {words.length}</span>
            </div>
          </div>
          {words.length > 0 && (
            <button
              type="button"
              className={styles.startLearningBtn}
              onClick={handleStartWordStudy}
            >
              去学习
            </button>
          )}
        </div>

        <div className={styles.wordsContainer}>
          <div className={styles.wordsSection}>
            <WordsSection
              wordsLoading={false}
              chapterWords={words}
              showAllWordDefinitions={wordShowDefinitions}
              onToggleAllWordDefinitions={() => setWordShowDefinitions(prev => !prev)}
              showAllWordSentences={wordShowSentences}
              onToggleAllWordSentences={() => setWordShowSentences(prev => !prev)}
              expandedSentences={expandedSentences}
              onGoToChapter={(chapterId, sentenceId) => handleGoToChapterWithHighlight(chapterId, sentenceId)}
              favoriteLoadingMap={wordFavoriteLoadingMap}
              onFavoriteToggle={handleDeleteWord}
              onShowWordDetail={handleOpenWordModal}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <button
          type="button"
          className={styles.backButton}
          onClick={handleBackToHistory}
          aria-label="返回收藏"
        >
          <ArrowLeftIcon size={20} />
        </button>
        <span className={styles.topTitle}>收藏单词</span>
      </header>
      <main className={styles.content}>
        {renderContent()}
        {chapterMetadataLoading && (
          <div className={styles.metadataHint}>章节信息加载中...</div>
        )}
        {chapterMetadataError && (
          <div className={styles.metadataError}>{chapterMetadataError}</div>
        )}
      </main>
      <WordModal
        isOpen={wordModalOpen}
        wordLookup={selectedWordLookup}
        activeDict={wordModalActiveDict}
        dictLoading={mergedWordModalLoading}
        dictResults={wordModalDictResults}
        favoriteLoading={wordModalFavoriteLoading}
        isFavorited={wordModalFavorited}
        onPlayPronunciation={(voiceType) =>
          selectedWordLookup?.word && handleWordModalPronounce(selectedWordLookup.word, voiceType)
        }
        onFavoriteWord={handleWordModalFavoriteToggle}
        onDictTabClick={handleWordModalDictTabClick}
        onDefinitionClick={() => {}}
        onClose={handleCloseWordModal}
      />
    </div>
  );
};

export default BookWordList;
